import App from '@scripts/app'
import Block from './Block'
import gsap from 'gsap'
import { Draggable } from 'gsap/Draggable'

export default class BlockGridDrag extends Block {
  constructor(el) {
    super(el)

    this.isHover = false
    this.isDisabled = true
    this.isWheeling = false
  }

  getElems() {
    super.getElems()

    this.content = this.el.querySelector('.js-grid-gallery-drag-content')
    this.items = this.el.querySelectorAll('.js-grid-gallery-drag-item')
  }

  bindMethods() {
    super.bindMethods()

    this.itemEnter = this.itemHover.bind(this, true)
    this.itemLeave = this.itemHover.bind(this, false)
  }

  events() {
    super.events()

    this.items.forEach((item) => {
      item.picture = item.querySelector('picture')
      item.picture.title = item.dataset.title
      item.picture.subtitle = item.dataset.subtitle

      this.addEvent({
        target: item.picture, name: 'mouseenter', callback: this.itemEnter
      })

      this.addEvent({
        target: item.picture, name: 'mouseleave', callback: this.itemLeave
      })
    })

    this.addEvent({
      target: this.content, name: 'mousewheel', callback: this.wheel
    })

    this.addEvent({
      target: document, name: 'keyup', callback: this.keyPress
    })
  }

  onEnterCompleted() {
    super.onEnterCompleted()

    this.createDraggable()
  }

  /* /////////////////////////////////////////
  //  DRAGGABLE
  ///////////////////////////////////////// */
  createDraggable() {
    this.dragX = 0
    this.gapX = (App.windowWidth / 2) - (this.items[0].offsetWidth / 2)

    gsap.set(this.content, { paddingLeft: this.gapX, paddingRight: this.gapX })

    const draggable = Draggable.create(this.content, {
      type: 'x',
      bounds: this.el,
      inertia: false,
      edgeResistance: 0.6,
      dragResistance: 0.7,
      throwResistance: 0.0000001,
      zIndexBoost: false,
      throwProps: true,
      onPress: () => {
        if (!this.isHover) {
          this.dragPress(true)
          App.cursor.togglePressed(true)
        }
      },
      onDragStart: () => {
        this.dragDisabled(true)
        this.dragPress(true)
      },
      onRelease: () => {
        this.dragPress(false)
        App.cursor.togglePressed(false)
      },
      onThrowComplete: () => this.dragDisabled(false),
      onThrowUpdate: () => this.dragUpdate(),
      onDrag: () => this.dragUpdate(),
      snap: {
        x: []
      },
      liveSnap: {
        x: (value) => {
          this.dragX = -value
          return value
        }
      }
      // allowEventDefault: !App.isTouch()
    })

    this.draggable = draggable[0]

    this.setSnap()

    this.addTween(this.draggable)
  }

  setSnap() {
    this.hSnap = []
    let x = 0

    this.hSnap.push(x)

    this.items.forEach((item) => {
      x -= item.offsetWidth

      const newX = x > this.draggable.minX ? x : this.draggable.minX

      this.hSnap.push(newX)
    })

    this.hSnap = this.hSnap.filter((x, i) => this.hSnap.indexOf(x) === i)

    this.draggable.vars.snap.x = this.hSnap
  }

  dragUpdate() {
    const x = Math.trunc(gsap.getProperty(this.content, 'x'))

    this.oParent.setPanelX(x)
  }

  dragUpdateForce(bool) {
    let newIndex = null
    const currentIndex = this.hSnap.indexOf(this.draggable.x)

    if (bool < 0) {
      if (currentIndex == 0) return
      newIndex = currentIndex - 1
    } else {
      if (currentIndex == (this.hSnap.length - 1)) return
      newIndex = currentIndex + 1
    }

    this.isWheeling = true
    this.dragPress(true)

    this.tlForce = gsap.timeline({
      onComplete: () => {
        this.draggable.update()
        this.isWheeling = false
      }
    })

    this.tlForce.to(this.content, { x: this.hSnap[newIndex], duration: 1, ease: 'power2.inOut' }, 'start')
    this.tlForce.add(() => {
      this.dragPress(false)
    }, '-=0.4')
  }

  dragDisabled(bool) {
    this.isDragDisabled = this.el.classList.toggle('disabled', bool)
  }

  dragPress(bool) {
    this.el.classList.toggle('pressed', bool)
  }

  disabled(bool) {
    this.isDisabled = this.el.classList.toggle('pointer-events-none', bool)
  }

  reset() {
    gsap.set(this.content, { x: 0 })
    this.draggable.update()
  }

  /* /////////////////////////////////////////
  //  ITEMS
  ///////////////////////////////////////// */

  itemHover(bool, e) {
    if (this.isDisabled) return

    this.isHover = bool

    if (bool) this.oParent.titleFill(e.currentTarget.title, e.currentTarget.subtitle)

    if (this.tlHover) this.tlHover.kill()

    this.tlHover = gsap.timeline()
    this.tlHover.add(this.oParent.titleToggle(!bool), 'start')
    this.tlHover.add(this.oParent.titleSecondToggle(bool), 'start')
  }

  appearItems(bool) {
    return gsap.to(this.items, { duration: bool ? 1 : 0.5, opacity: bool ? 1 : 0, ease: 'quad.out' })
  }

  /* /////////////////////////////////////////
  //  EVENTS
  ///////////////////////////////////////// */
  wheel(e) {
    if (!App.isDevice('laptop-min') || this.isDisabled || this.isDragDisabled || this.isWheeling) return

    this.dragUpdateForce(e.deltaY)
  }

  keyPress(e) {
    if (!App.isDevice('laptop-min') || this.isDisabled || this.isDragDisabled || this.isWheeling) return

    if (e.which == 39 || e.which == 40) this.dragUpdateForce(1)
    if (e.which == 37 || e.which == 38) this.dragUpdateForce(-1)
  }

}
